/**
 * useColorMode Hook
 *
 * Manages color mode (light/dark/system) with localStorage persistence
 * and applies the appropriate class to the document root element.
 *
 * Uses CSS transitions for smooth color changes (defined in index.css).
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

export type ColorMode = 'light' | 'dark' | 'system';

interface UseColorModeReturn {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  resolvedColorMode: 'light' | 'dark';
}

/**
 * Get the system color scheme preference
 */
function getSystemPreference(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Apply the color mode class to the document root.
 * CSS transitions handle the animation (defined in index.css).
 */
function applyColorMode(mode: ColorMode): 'light' | 'dark' {
  const root = document.documentElement;
  const resolved = mode === 'system' ? getSystemPreference() : mode;
  const other = resolved === 'dark' ? 'light' : 'dark';

  root.classList.add(resolved);
  root.classList.remove(other);

  return resolved;
}

export function useColorMode(): UseColorModeReturn {
  const [storedMode, setStoredMode] = useLocalStorage<ColorMode>(
    'color-mode',
    'system',
  );
  const [colorMode, setColorModeState] = useState<ColorMode>(storedMode);

  // Calculate resolved color mode
  const getResolvedMode = useCallback((): 'light' | 'dark' => {
    return colorMode === 'system' ? getSystemPreference() : colorMode;
  }, [colorMode]);

  const [resolvedColorMode, setResolvedColorMode] = useState<'light' | 'dark'>(
    getResolvedMode,
  );

  // Apply color mode on mount and when it changes
  useEffect(() => {
    applyColorMode(colorMode);
  }, [colorMode]);

  // Sync resolved color mode when colorMode changes
  const [prevColorMode, setPrevColorMode] = useState(colorMode);
  if (prevColorMode !== colorMode) {
    setPrevColorMode(colorMode);
    setResolvedColorMode(getResolvedMode());
  }

  // Listen for system preference changes when in system mode
  useEffect(() => {
    if (colorMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const resolved = applyColorMode('system');
      setResolvedColorMode(resolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [colorMode]);

  const setColorMode = useCallback(
    (mode: ColorMode) => {
      setColorModeState(mode);
      setStoredMode(mode);
    },
    [setStoredMode],
  );

  return {
    colorMode,
    setColorMode,
    resolvedColorMode,
  };
}
